import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CodeField from './CodeField'
import { isFullCode } from './codes'
import { AUTH_ERROR, isLoginOver, readAuthError } from '../../services/authErrors'
import { heldClock } from '../../utils/loginErrorMessage'

/**
 * VH-285 — asks for a code from the app, or a recovery code, sends it and
 * says what the server answered. The login's second step and the dialog
 * before a sensitive action are both this form (FinanceHub FH-104).
 *
 * `send({ code })` or `send({ recoveryCode })` does the call. What is typed is
 * emptied after every answer — a code works once — except after a network
 * error, when it may still be good. `onExpired` when the login ran out,
 * `onDone(data)` when the code passed.
 */
export default function CodeForm({
  idPrefix,
  send,
  onDone,
  onExpired,
  submitLabel,
  allowRecovery = true,
  children,
}) {
  const { t } = useTranslation()
  const [recovery, setRecovery] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [attempt, setAttempt] = useState(0)

  const ready = recovery ? value.trim().length > 0 : isFullCode(value)
  const errorId = `${idPrefix}-error`

  const submit = async (event) => {
    event.preventDefault()
    if (!ready || busy) return
    setError(null)
    setBusy(true)
    try {
      const { data } = await send(recovery ? { recoveryCode: value.trim() } : { code: value })
      onDone(data)
    } catch (err) {
      if (onExpired && isLoginOver(err)) {
        onExpired()
        return
      }
      const { kind, status } = readAuthError(err)
      if (kind === AUTH_ERROR.NETWORK) {
        setError(t('secondFactor.code.unreachable'))
      } else {
        setValue('')
        if (kind === AUTH_ERROR.WRONG_CODE) {
          setError(t(recovery ? 'secondFactor.code.recoveryWrong' : 'secondFactor.code.wrong'))
        } else if (kind === AUTH_ERROR.CODE_USED) {
          setError(t('secondFactor.code.usedBefore'))
        } else if (kind === AUTH_ERROR.HELD) {
          const until = heldClock(err)
          setError(until ? t('auth.heldUntil', { until }) : t('auth.loginRefused'))
        } else {
          setError(t('secondFactor.code.failed', { status }))
        }
      }
      setAttempt((n) => n + 1)
    } finally {
      setBusy(false)
    }
  }

  const switchKind = () => {
    setRecovery((on) => !on)
    setValue('')
    setError(null)
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <CodeField
        id={`${idPrefix}-code`}
        label={t(recovery ? 'secondFactor.code.recoveryLabel' : 'secondFactor.code.label')}
        hint={t(recovery ? 'secondFactor.code.recoveryHint' : 'secondFactor.code.hint')}
        value={value}
        onChange={setValue}
        recovery={recovery}
        disabled={busy}
        invalid={Boolean(error)}
        describedBy={error ? errorId : undefined}
        focusKey={attempt}
      />

      <div aria-live="assertive">
        {error && (
          <p id={errorId} role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        {children}
        <button type="submit" disabled={!ready || busy} className="btn-primary w-full sm:flex-1">
          {busy ? t('secondFactor.code.checking') : submitLabel || t('secondFactor.code.submit')}
        </button>
      </div>

      {allowRecovery && (
        <button
          type="button"
          onClick={switchKind}
          disabled={busy}
          className="text-left text-sm font-medium text-primary-600 underline hover:text-primary-500"
        >
          {t(recovery ? 'secondFactor.code.useApp' : 'secondFactor.code.useRecovery')}
        </button>
      )}
    </form>
  )
}
